// ChannelWriteService.js

// Firebase
import {
  deleteDoc,
  doc,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { collections, db, storage } from 'firebaseConfig';

// Services
import { assertWorkspaceUploadAllowed } from 'services/workspaces/WorkspaceUploadPolicyService';

// Utilities
import { normalizeChannelType } from 'channels/constants/channelTypes';
import { generateKey } from 'utilities/Keys';

const WORKSPACES_COLLECTION = collections.workspaces;

const channelDocRef = (workspaceKey, channelKey) => (
  doc(db, WORKSPACES_COLLECTION, workspaceKey, 'channels', channelKey)
);

const channelMessageDocRef = (workspaceKey, channelKey, messageKey) => (
  doc(db, WORKSPACES_COLLECTION, workspaceKey, 'channels', channelKey, 'messages', messageKey)
);

const channelReadDocRef = (workspaceKey, channelKey, userKey) => (
  doc(db, WORKSPACES_COLLECTION, workspaceKey, 'channels', channelKey, 'reads', userKey)
);

const toCleanText = (value) => String(value || '').trim();

const truncate = (value, max = 220) => {
  const text = String(value || '').trim();
  if (!text) return '';
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}...`;
};

const sanitizeFileName = (value = '') => (
  String(value || 'image')
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/_+/g, '_')
    .slice(0, 80)
);

/**
 * Creates a workspace channel.
 *
 * @param {object} options - Channel options.
 * @param {string} options.workspaceKey - Workspace key.
 * @param {string} options.userKey - User key.
 * @param {string} options.userName - User display name.
 * @param {string} options.title - Channel title.
 * @param {string|null} options.moduleKey - Optional module key.
 * @param {string} options.topic - Optional topic.
 * @param {string} options.channelType - Channel type key.
 *
 * @returns {Promise<object|null>} Created channel payload.
 */
export async function createChannel({
  workspaceKey,
  userKey,
  userName,
  title,
  moduleKey,
  topic,
  channelType,
} = {}) {
  const cleanTitle = toCleanText(title);
  const cleanTopic = toCleanText(topic);
  const cleanModuleKey = toCleanText(moduleKey) || null;
  const cleanChannelType = normalizeChannelType(channelType);

  if (!workspaceKey || !userKey || !cleanTitle) return null;

  const channelKey = generateKey();
  const now = Timestamp.now();
  const creatorKey = String(userKey || '').trim();

  const payload = {
    key: channelKey,
    workspaceKey,
    title: cleanTitle,
    titleLower: cleanTitle.toLowerCase(),
    moduleKey: cleanModuleKey,
    topic: cleanTopic,
    channelType: cleanChannelType,
    memberKeys: creatorKey ? [creatorKey] : [],
    createdBy: creatorKey,
    createdByName: userName || 'Workspace Member',
    dateCreated: now,
    dateModified: now,
    lastMessageAt: null,
    lastMessageByKey: null,
    lastMessageByName: null,
    lastMessagePreview: '',
  };

  await setDoc(channelDocRef(workspaceKey, channelKey), payload, { merge: false });
  return payload;
}

/**
 * Marks a channel as read for a user.
 *
 * @param {object} options - Read receipt options.
 * @param {string} options.workspaceKey - Workspace key.
 * @param {string} options.channelKey - Channel key.
 * @param {string} options.userKey - User key.
 * @param {Timestamp|Date|string} options.lastReadAt - Last read marker.
 *
 * @returns {Promise<void>}
 */
export async function markChannelRead({ workspaceKey, channelKey, userKey, lastReadAt } = {}) {
  if (!workspaceKey || !channelKey || !userKey) return;

  await setDoc(
    channelReadDocRef(workspaceKey, channelKey, userKey),
    {
      key: userKey,
      userKey,
      workspaceKey,
      channelKey,
      lastReadAt: lastReadAt || serverTimestamp(),
      dateModified: serverTimestamp(),
    },
    { merge: true }
  );
}

/**
 * Uploads image attachments for a channel message.
 *
 * @param {object} options - Upload options.
 * @param {string} options.workspaceKey - Workspace key.
 * @param {string} options.channelKey - Channel key.
 * @param {string} options.messageKey - Message key.
 * @param {File[]} options.files - Files to upload.
 *
 * @returns {Promise<object[]>} Uploaded image attachment rows.
 */
export async function uploadChannelImageAttachments({ workspaceKey, channelKey, messageKey, files = [] } = {}) {
  if (!workspaceKey || !channelKey || !messageKey) return [];
  if (!Array.isArray(files) || files.length === 0) return [];

  const uploads = files.map(async (file, index) => {
    if (!file) return null;

    await assertWorkspaceUploadAllowed({
      workspaceKey,
      fileName: file.name,
      fileSizeBytes: file.size,
    });

    const fileName = sanitizeFileName(file.name || `image-${index + 1}.jpg`);
    const path = `workspaces/${workspaceKey}/channels/${channelKey}/messages/${messageKey}/${index + 1}_${fileName}`;
    const objectRef = ref(storage, path);

    await uploadBytes(objectRef, file, {
      contentType: file.type || 'image/jpeg',
      cacheControl: 'public,max-age=3600',
    });

    const downloadURL = await getDownloadURL(objectRef);

    return {
      key: generateKey(),
      type: 'image',
      name: file.name || fileName,
      mimeType: file.type || 'image/jpeg',
      path,
      size: Number(file.size) || 0,
      url: downloadURL,
    };
  });

  const results = await Promise.all(uploads);
  return results.filter(Boolean);
}

/**
 * Creates a message in a channel and updates channel metadata.
 *
 * @param {object} options - Message options.
 * @param {string} options.workspaceKey - Workspace key.
 * @param {string} options.channelKey - Channel key.
 * @param {string} options.userKey - User key.
 * @param {object} options.user - Optional user summary snapshot.
 * @param {string} options.userName - User display name.
 * @param {string} options.userPhoto - Optional user photo URL.
 * @param {string} options.text - Message body.
 * @param {object[]} options.attachments - Message attachments.
 * @param {string} options.messageKey - Optional pre-generated message key.
 *
 * @returns {Promise<object|null>} Created message payload.
 */
export async function postChannelMessage({
  workspaceKey,
  channelKey,
  userKey,
  user,
  userName,
  userPhoto,
  text,
  attachments = [],
  messageKey,
} = {}) {
  const cleanText = String(text || '').trim();
  const cleanAttachments = Array.isArray(attachments) ? attachments.filter(Boolean) : [];

  if (!workspaceKey || !channelKey || !userKey) return null;
  if (!cleanText && cleanAttachments.length === 0) return null;

  const nextMessageKey = messageKey || generateKey();
  const now = Timestamp.now();
  const userSummary = user || null;
  const safeUserName = userSummary?.name || userName || 'Workspace Member';
  const safeUserPhoto = userSummary?.coverUrl || userPhoto || '';

  const payload = {
    key: nextMessageKey,
    workspaceKey,
    channelKey,
    userKey,
    user: userSummary,
    userName: safeUserName,
    userPhoto: safeUserPhoto,
    text: cleanText,
    attachments: cleanAttachments,
    isEdited: false,
    dateCreated: now,
    dateModified: now,
    dateDeleted: null,
  };

  const batch = writeBatch(db);
  batch.set(channelMessageDocRef(workspaceKey, channelKey, nextMessageKey), payload, { merge: false });

  const previewText = cleanText || (cleanAttachments.length > 0 ? 'Shared' : 'Sent a message');

  batch.update(channelDocRef(workspaceKey, channelKey), {
    dateModified: now,
    lastMessageAt: now,
    lastMessageByKey: userKey,
    lastMessageByName: safeUserName,
    lastMessagePreview: truncate(previewText),
  });

  await batch.commit();
  return payload;
}

/**
 * Edits an existing channel message.
 *
 * @param {object} options - Edit options.
 * @param {string} options.workspaceKey - Workspace key.
 * @param {string} options.channelKey - Channel key.
 * @param {string} options.messageKey - Message key.
 * @param {string} options.text - Updated message text.
 *
 * @returns {Promise<void>}
 */
export async function editChannelMessage({ workspaceKey, channelKey, messageKey, text } = {}) {
  const cleanText = toCleanText(text);
  if (!workspaceKey || !channelKey || !messageKey || !cleanText) return;

  const now = Timestamp.now();

  await updateDoc(channelMessageDocRef(workspaceKey, channelKey, messageKey), {
    text: cleanText,
    isEdited: true,
    dateModified: now,
  });

  await updateDoc(channelDocRef(workspaceKey, channelKey), {
    dateModified: now,
    lastMessagePreview: truncate(cleanText),
  });
}

/**
 * Soft deletes an existing channel message.
 *
 * @param {object} options - Delete options.
 * @param {string} options.workspaceKey - Workspace key.
 * @param {string} options.channelKey - Channel key.
 * @param {string} options.messageKey - Message key.
 * @param {string} options.userKey - User key.
 *
 * @returns {Promise<void>}
 */
export async function deleteChannelMessage({ workspaceKey, channelKey, messageKey, userKey } = {}) {
  if (!workspaceKey || !channelKey || !messageKey) return;

  const now = Timestamp.now();

  await updateDoc(channelMessageDocRef(workspaceKey, channelKey, messageKey), {
    text: '',
    attachments: [],
    dateDeleted: now,
    deletedBy: userKey || null,
    dateModified: now,
  });

  await updateDoc(channelDocRef(workspaceKey, channelKey), {
    dateModified: now,
    lastMessagePreview: 'Message deleted',
  });
}

/**
 * Updates channel properties.
 *
 * @param {object} options - Update options.
 * @param {string} options.workspaceKey - Workspace key.
 * @param {string} options.channelKey - Channel key.
 * @param {string} options.title - Channel title.
 * @param {string} options.topic - Channel description/topic.
 *
 * @returns {Promise<void>}
 */
export async function updateChannel({ workspaceKey, channelKey, title, topic } = {}) {
  const cleanTitle = toCleanText(title);
  const cleanTopic = toCleanText(topic);

  if (!workspaceKey || !channelKey || !cleanTitle) return;

  await updateDoc(channelDocRef(workspaceKey, channelKey), {
    title: cleanTitle,
    titleLower: cleanTitle.toLowerCase(),
    topic: cleanTopic,
    dateModified: Timestamp.now(),
  });
}

/**
 * Updates channel membership keys.
 *
 * @param {object} options - Update options.
 * @param {string} options.workspaceKey - Workspace key.
 * @param {string} options.channelKey - Channel key.
 * @param {string[]} options.memberKeys - Next channel member keys.
 *
 * @returns {Promise<void>}
 */
export async function updateChannelMembers({ workspaceKey, channelKey, memberKeys = [] } = {}) {
  if (!workspaceKey || !channelKey) return;

  const cleanMemberKeys = Array.from(new Set(
    (Array.isArray(memberKeys) ? memberKeys : [])
      .map((key) => String(key || '').trim())
      .filter(Boolean)
  ));

  await updateDoc(channelDocRef(workspaceKey, channelKey), {
    memberKeys: cleanMemberKeys,
    dateModified: Timestamp.now(),
  });
}

/**
 * Deletes a channel document.
 *
 * @param {object} options - Delete options.
 * @param {string} options.workspaceKey - Workspace key.
 * @param {string} options.channelKey - Channel key.
 *
 * @returns {Promise<void>}
 */
export async function deleteChannel({ workspaceKey, channelKey } = {}) {
  if (!workspaceKey || !channelKey) return;
  await deleteDoc(channelDocRef(workspaceKey, channelKey));
}
